import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ProgressSteps, MainContentPaddingTypes, NewModal } from "../..";
import { glyphs, useMediaQuery } from "../../../Styles";
import NewSectionModalLvl1 from "./newSectionModalLvl1";
import NewSectionModalLvl2 from "./newSectionModalLvl2";
import NewSectionModalLvl3 from "./newSectionModalLvl3";
import { CreatePanelLocation } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/createLfSectionCall";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { Section } from "./_Modals/newSectionModalInterfaces";
import { handleSectionLevel3Click } from "./_Modals/handleNewSectionLevel3Click";
import { useTerminalTypes } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useTerminalTypes";

interface NewSectionModal_Props {
  isModalOpen: boolean
  closeModal: () => void,
  systemId: number;
}

export default function NewSectionModal({ isModalOpen, closeModal, systemId }: NewSectionModal_Props) {
  const { t } = useTranslation()
  const [currentSectionLevel, setCurrentSectionLevel] = useState(1);
  const [section, setSection] = useState<Section>({ pens: [], feederOnly: true, lanController: "AICO_LAN1", columns: 2 });
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({ domain: farm })), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { createSectionLocations } = useSection(connection);
  const { data: terminalTypesState, call: getTerminalTypes } = useTerminalTypes(connection);
  useEffect(() => {
    if (!terminalTypesState[domain]) {
      getTerminalTypes(undefined)
    }
  }, [domain, getTerminalTypes, terminalTypesState])

  
  const handleSaveSection = (newSection: CreatePanelLocation[], sectionName: string, panelSectionType: number) => {
    return createSectionLocations(connection.connection.domain, sectionName, panelSectionType, systemId, newSection);
  }

  // Function to handle Quick Entry Modal based on level
  const ShowQuickEntry = (level: 0 | 1 | 2 | 3) => {
    setCurrentSectionLevel(level);
  };
  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setCurrentSectionLevel(1); // Reset to Main Info Modal
    }
  }, [isModalOpen]);

  // Function to handle "Go Back"
  const onGoBack = () => {
    if (currentSectionLevel === 3) {
      ShowQuickEntry(2); // Go back to Level 2
    } else if (currentSectionLevel === 2) {
      ShowQuickEntry(1); // Go back to Level 1
    }
  }

  return (
    <NewModal
      modalState={isModalOpen}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary,
        headerContentHeight: "65px"
      }}
      predefinedButtons={{
        onClose: closeModal,
        onGoBack: currentSectionLevel === 2 || currentSectionLevel === 3 ? onGoBack : undefined
      }}
      header={t("locationFeederPages.sections.newSection", { defaultValue: "new section" })}
      headerContent={
        <ProgressSteps
          titles={[t("devices.layout", { defaultValue: "layout" }), t("devices.ioBoards", { defaultValue: "i/o boards" }), t("buttons.apply", { defaultValue: "apply" })]}
          icons={[glyphs.Animal({}), glyphs.Animal({}), glyphs.Animal({})]}
          opacityValues={currentSectionLevel === 1 ? [1, 0.5, 0.5] : currentSectionLevel === 2 ? [0.5, 1, 0.5] : [0.5, 0.5, 1]}
        />
      }
      secondaryContent={currentSectionLevel === 3 ?
        <div style={{ width: mobileXs ? "290px" : "615px" }}>
          {<NewSectionModalLvl3 section={section} />}
        </div>
        : undefined
      }
      mainContent={
        <div style={{ minWidth: mobileXs ? "290px" : "615px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          {currentSectionLevel === 1 && <NewSectionModalLvl1 section={section} onSectionChange={setSection} />} {/* Select Modal */}
          {currentSectionLevel === 2 && <NewSectionModalLvl2 section={section} onSectionChange={setSection} />} {/* Action Modal */}
          <Button 
            content={currentSectionLevel === 1||currentSectionLevel === 2 ? t("buttons.next", {defaultValue: "next"}) : t("buttons.create", {defaultValue: "create"})}
            onClick={currentSectionLevel === 1 ? () => setCurrentSectionLevel(2) : currentSectionLevel === 2 ? () => setCurrentSectionLevel(3) : () => handleSectionLevel3Click(section, closeModal, t("buttons.confirm", {defaultValue: "confirm"}), handleSaveSection) } // TODO CTO: On closeModal() also save to db/create report
          />
        </div>
      }
    />
  );
}

